// MACHINERY V2 (Stroje) — profile tab bodies: the router between the five tabs.
//
// M2.4 shipped this file as five marked placeholders. M2.5 replaced four of
// them with the real surfaces; the fifth is still a placeholder BY SCHEDULE:
//   live            -> REPLACE AT M2.10  (MashLiveLedger — the signal ledger;
//                      backend-gated, ticket §6/§7. The dark "feed controls"
//                      simulator rig in artboard 0601 is prototype-only and is
//                      NOT built, ever — ticket §4.2.)
//   serviceReports  -> ServiceReportsTabV2   (M2.5, artboards 1000-1002, 1012)
//   documents       -> DocumentsTabV2        (M2.5, artboards 1003-1005, 1014)
//   specifications  -> SpecificationsTabV2   (M2.5, artboards 1006-1008, 1015)
//   parts           -> PartsTabV2            (M2.5, artboards 1009-1011, 1013)
//
// THE SPECIFICATIONS BRANCH IS NOT A GATE. `buildProfileTabs` (profileTabContract
// .ts) only emits "specifications" when the machine HAS a `catalogModelId`, so
// this component can never be asked for that tab without one. The `if` below is
// a TYPE narrowing (`string | undefined` -> `string`) plus a defensive null
// render, not a second copy of the rule — a second copy is exactly what the
// contract file exists to prevent.
//
// EACH TAB OWNS ITS OWN FETCH. There is no prefetch here and no shared loading
// state: a tab that is not rendered must not cost a request, and the four
// surfaces read four different slices.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Machine } from "../../models/models";
import {
  MachineryV2ProfileTab,
  PROFILE_TAB_LABEL_KEYS,
} from "./profileTabContract";
import ServiceReportsTabV2 from "./tabs/ServiceReportsTabV2";
import DocumentsTabV2 from "./tabs/DocumentsTabV2";
import SpecificationsTabV2 from "./tabs/SpecificationsTabV2";
import PartsTabV2 from "./tabs/PartsTabV2";

export interface ProfileTabBodyProps {
  tab: MachineryV2ProfileTab;
  machine: Machine;
}

/** The honest "being rebuilt" card. Only the Live tab still uses it (M2.10). */
const TabPlaceholder: React.FC<{ tab: MachineryV2ProfileTab }> = ({ tab }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div
      style={{
        background: "var(--bg-soft)",
        border: "1px dashed var(--border)",
        borderRadius: 12,
        padding: "28px 20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        textAlign: "center",
        minHeight: 220,
        minWidth: 0,
      }}
    >
      <div className="eyebrow">{translate(PROFILE_TAB_LABEL_KEYS[tab])}</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink-2)" }}>
        {translate("myMachinery:v2.profile.comingSoon")}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--muted)", maxWidth: 420 }}>
        {translate("myMachinery:v2.profile.comingSoonHint")}
      </div>
    </div>
  );
};

const ProfileTabBody: React.FC<ProfileTabBodyProps> = ({ tab, machine }) => {
  if (tab === "serviceReports") return <ServiceReportsTabV2 machineId={machine.id} />;
  if (tab === "documents") return <DocumentsTabV2 machineId={machine.id} />;
  if (tab === "parts") return <PartsTabV2 machineId={machine.id} />;
  if (tab === "specifications") {
    if (!machine.catalogModelId) return null;
    return <SpecificationsTabV2 catalogModelId={machine.catalogModelId} />;
  }
  return <TabPlaceholder tab={tab} />;
};

export default ProfileTabBody;
